export const HONEY_COMBAT = Object.freeze({
  fireRateMs: 1200,
  damage: 3,
  slowAmount: 0.4,
  slowDurationMs: 2000,
  projectileSpeed: 200,
})

export interface Point {
  x: number
  y: number
}

export interface HoneyTargetCandidate extends Point {
  id: number
  progress: number
  alive: boolean
}

export function selectHoneyTarget(
  tower: Point,
  candidates: readonly HoneyTargetCandidate[],
  range: number,
): number | null {
  const rangeSquared = range * range
  const eligible = candidates.filter(candidate => (
    candidate.alive
    && (candidate.x - tower.x) ** 2 + (candidate.y - tower.y) ** 2 <= rangeSquared
  ))
  eligible.sort((a, b) => b.progress - a.progress || a.id - b.id)
  return eligible[0]?.id ?? null
}

export interface HoneyCooldownState {
  remainingMs: number
}

export function createHoneyCooldown(): HoneyCooldownState {
  return { remainingMs: 0 }
}

export function advanceHoneyCooldown(
  state: HoneyCooldownState,
  deltaMs: number,
  hasTarget: boolean,
): { state: HoneyCooldownState; fired: boolean } {
  const remainingMs = Math.max(0, state.remainingMs - Math.max(0, deltaMs))
  if (!hasTarget || remainingMs > 0) return { state: { remainingMs }, fired: false }
  return { state: { remainingMs: HONEY_COMBAT.fireRateMs }, fired: true }
}

export interface HoneyFishLoanState {
  lent: readonly [boolean, boolean]
  nextIndex: 0 | 1
}

export function createHoneyFishLoans(): HoneyFishLoanState {
  return { lent: [false, false], nextIndex: 0 }
}

export function borrowHoneyFish(state: HoneyFishLoanState): {
  state: HoneyFishLoanState
  fishIndex: 0 | 1 | null
} {
  const preferred = state.nextIndex
  const alternate = preferred === 0 ? 1 : 0
  const fishIndex = !state.lent[preferred]
    ? preferred
    : !state.lent[alternate] ? alternate : null
  if (fishIndex === null) return { state, fishIndex: null }
  const lent: [boolean, boolean] = [...state.lent]
  lent[fishIndex] = true
  return {
    state: { lent, nextIndex: fishIndex === 0 ? 1 : 0 },
    fishIndex,
  }
}

export function returnHoneyFish(state: HoneyFishLoanState, fishIndex: 0 | 1): HoneyFishLoanState {
  if (!state.lent[fishIndex]) return state
  const lent: [boolean, boolean] = [...state.lent]
  lent[fishIndex] = false
  return { ...state, lent }
}

export interface HoneyEnemyCombatState {
  hp: number
  alive: boolean
  slowRemainingMs: number
}

export function applyHoneyHit<T extends HoneyEnemyCombatState>(enemy: T): T {
  const hp = Math.max(0, enemy.hp - HONEY_COMBAT.damage)
  return {
    ...enemy,
    hp,
    alive: hp > 0,
    slowRemainingMs: HONEY_COMBAT.slowDurationMs,
  }
}

export function tickHoneyEnemy<T extends HoneyEnemyCombatState>(enemy: T, deltaMs: number): T {
  return {
    ...enemy,
    slowRemainingMs: Math.max(0, enemy.slowRemainingMs - Math.max(0, deltaMs)),
  }
}
